import { PlayArrowOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { confirmWillSourceVehicle } from "../../../api/dossier/confirmWillSourceVehicle";
import { requestDossierVehiclePricing } from "../../../api/dossier/requestDossierVehiclePricing";
import { getListQuote } from "../../../api/quote/getListQuote";
import { getQuoteOptions } from "../../../api/quote/getQuoteOptions";
import { getQuoteVehicleInfo } from "../../../api/quote/getQuoteVehicleInfo";
import { getVehicleImages } from "../../../api/vehicle/getVehicleImages";
import { getVehicleVariant } from "../../../api/vehicle/getVehicleVariant";
import APInfoText from "../../../components/APInfoText";
import { APButtonLink } from "../../../components/buttons/APButtonLink";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import BackButton from "../../../components/buttons/BackButton";
import HomeButton from "../../../components/buttons/HomeButton";
import InfoButton from "../../../components/buttons/InfoButton";
import APCheckbox from "../../../components/form/APCheckbox";
import APFileUpload from "../../../components/form/APFileUpload";
import APForm from "../../../components/form/APForm";
import APTextField from "../../../components/form/APTextField";
import PageContainer from "../../../components/PageContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { filterSelectedOptions } from "../../../helpers/quotes";
import { useFaqs } from "../../../hooks/useFaqs";
import { useVehicleSourcingAccess } from "../../../hooks/useVehicleSourcingAccess";
import { useAuth } from "../../../providers/AuthProvider";
import { useConfirm } from "../../../providers/ConfirmProvider";
import { ACTION_CONFIRMATION_EVENT_IDS } from "../../../types/Dossier";
import APVehicleDetail from "../../vehicleDetail/components/VehicleDetail";

type VehicleSourcingConfirmConfigurationInputs = {
  exteriorColour: string;
  interiorColour: string;
  driverVehicleQuoteFile: File | null;
  confirm: boolean;
};

const VehicleSourcingConfirmConfiguration = () => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const [showVehiclePricingUpload, setShowVehiclePricingUpload] =
    useState(false);
  const confirm = useConfirm();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VehicleSourcingConfirmConfigurationInputs>({ mode: "onTouched" });

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const {
    dossier,
    navigationCheckComplete,
    isLoading: isDossierLoading,
    isSuccess: isDossierSuccess,
  } = useVehicleSourcingAccess({ dossierId });

  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;
  const primaryQuote = getListQuote(primaryQuoteId ?? "", {
    disabled: !primaryQuoteId,
  });
  const quoteVehicleInfo = getQuoteVehicleInfo(primaryQuoteId ?? "", {
    disabled: !primaryQuoteId,
  });
  const quoteOptions = getQuoteOptions(primaryQuoteId ?? "", {
    disabled: !primaryQuoteId,
  });
  const vehicleVariant = getVehicleVariant(
    primaryQuote.data?.vehicle.variantCode ?? "",
    { disabled: !primaryQuote.data }
  );
  const vehicleImages = getVehicleImages(
    primaryQuote.data?.vehicle.variantCode ?? "",
    { disabled: !primaryQuote.data }
  );

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "confirm-vehicle-options-to-source",
    contextIds: {
      opportunityId: dossier?.data?.opportunity?.id,
    },
  });

  const {
    mutateAsync: requestDossierVehiclePricingAsync,
    isLoading: isRequestingDossierVehiclePricing,
  } = requestDossierVehiclePricing(dossierId ?? "", {
    mutationOptions: {
      successNavPath: getScopedPageUrl(
        "dossierActionConfirmation",
        dossierId,
        ACTION_CONFIRMATION_EVENT_IDS.VEHICLE_SOURCING_REQUEST_VEHICLE_PRICING
      ),
    },
  });

  const {
    mutateAsync: confirmWillSourceVehicleAsync,
    isLoading: isConfirmingWillSourceVehicle,
  } = confirmWillSourceVehicle(dossierId ?? "", {
    mutationOptions: {
      successNavPath: `${getScopedPageUrl(
        "dossierActionConfirmation",
        dossierId,
        ACTION_CONFIRMATION_EVENT_IDS.VEHICLE_SOURCING_DRIVER_WILL_SOURCE_VEHICLE
      )}?nextActionIsHome=true`,
    },
  });

  const isLoading =
    isDossierLoading ||
    primaryQuote.isLoading ||
    quoteVehicleInfo.isLoading ||
    quoteOptions.isLoading ||
    vehicleVariant.isLoading ||
    vehicleImages.isLoading ||
    isRequestingDossierVehiclePricing ||
    isConfirmingWillSourceVehicle ||
    isLoadingFaqs;
  const isSuccess =
    isDossierSuccess &&
    primaryQuote.isSuccess &&
    quoteVehicleInfo.isSuccess &&
    quoteOptions.isSuccess &&
    vehicleVariant.isSuccess &&
    vehicleImages.isSuccess &&
    isSuccessFaqs;

  const driverSourcedVehicle =
    dossier.data?.opportunity?.driverSourcedVehicle ?? false;

  const onSubmit = async (data: VehicleSourcingConfirmConfigurationInputs) => {
    if (driverSourcedVehicle) {
      const ok = await confirm({
        title: "Finalise Vehicle Configuration?",
        description:
          "Are you sure you want to finalise the vehicle configuration? This will lock vehicle and options if you proceed.",
        cancelText: "No",
      });

      if (ok) {
        confirmWillSourceVehicleAsync({
          driverVehicleQuoteFile: data.driverVehicleQuoteFile ?? null,
        });
      }
    } else {
      const ok = await confirm({
        title: "Request Pricing for this Vehicle?",
        description: (
          <Stack spacing={1}>
            <Typography variant="labelLarge">
              You are about to request vehicle pricing on this exact vehicle
              configuration.
            </Typography>
            <Typography variant="labelLarge">
              You can only do this once.
            </Typography>
            <Typography variant="labelLarge">Are you sure?</Typography>
          </Stack>
        ),
        cancelText: "No",
      });

      if (ok) {
        requestDossierVehiclePricingAsync({
          requestedExteriorColour: data.exteriorColour,
          requestedInteriorColour: data.interiorColour,
          driverVehicleQuoteFile: data.driverVehicleQuoteFile ?? null,
        });
      }
    }
  };

  const onToggleVehiclePricingUpload = () => {
    setShowVehiclePricingUpload(!showVehiclePricingUpload);
  };

  const onEditVehicle = () => {
    navigate(
      getScopedPageUrl("dossierVehicleSourcingSelectVehicle", dossierId)
    );
  };

  const onEditOptions = () => {
    navigate(getScopedPageUrl("dossierVehicleSourcingOptions", dossierId));
  };

  const onEditManufacturerOptions = () => {
    navigate(
      getScopedPageUrl("dossierVehicleSourcingManufacturerOptions", dossierId)
    );
  };

  const topText = driverSourcedVehicle ? (
    <Typography variant="labelLarge">
      Please confirm the vehicle configuration and let us know when you have
      pricing.
    </Typography>
  ) : (
    <Box display="flex" flexDirection="column">
      <Typography variant="labelLarge">
        Please provide a final confirmation on the vehicle, preferences and
        options so we can obtain pricing and availability.
      </Typography>
      <Typography variant="labelLarge">This is the last step.</Typography>
    </Box>
  );

  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      loading={isLoading || !navigationCheckComplete}
      title={
        driverSourcedVehicle
          ? "Confirm Vehicle Configuration"
          : "Request Vehicle Pricing"
      }
      titleButton={
        <Box>
          {renderAppLocation(
            "ConfirmVehicleOptionsToSource-Page",
            <InfoButton />,
            true,
            true
          )}
        </Box>
      }
    >
      {isSuccess && navigationCheckComplete && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="labelLarge">{topText}</Typography>

            <APForm
              onSubmit={handleSubmit(onSubmit)}
              submitText="Next"
              submitIcon={<PlayArrowOutlined />}
              isLoading={false}
              isError={Object.keys(errors).length > 0}
              leftButton={
                <Box
                  sx={{
                    display: "flex",
                    columnGap: 1,
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                  >
                    <BackButton />
                    <HomeButton destination="dossiersHome" />
                  </Box>
                </Box>
              }
            >
              <APVehicleDetail
                vehicleVariant={vehicleVariant.data}
                vehicleImages={vehicleImages.data}
                vehicleOptions={filterSelectedOptions(
                  quoteOptions.data,
                  quoteVehicleInfo.data
                )}
                shouldPromptForAIConsent={false}
                onAISuggestions={() => {}}
                onAIReview={() => {}}
                priceToBeConfirmed={true}
                displaySections={{
                  images: true,
                  specs: true,
                  details: true,
                  options: true,
                  manufacturerOptions: true,
                  standardFeatures: false,
                  aiReview: false,
                  aiSuggestions: false,
                }}
                slots={{
                  belowDetails: driverSourcedVehicle ? undefined : (
                    <Stack spacing={2} my={2}>
                      <APTextField<VehicleSourcingConfirmConfigurationInputs>
                        name="exteriorColour"
                        label="Exterior Colour Preference"
                        control={control}
                        validations={{ required: true }}
                        errors={errors}
                        defaultValue=""
                        multiline
                        minRows={1}
                      />

                      <APTextField<VehicleSourcingConfirmConfigurationInputs>
                        name="interiorColour"
                        label="Interior Colour Preference"
                        control={control}
                        validations={{ required: true }}
                        errors={errors}
                        defaultValue=""
                        multiline
                        minRows={1}
                      />
                    </Stack>
                  ),
                }}
                hideExpandCollapseAllButton={true}
                editActions={{
                  onEditVehicle,
                  onEditOptions,
                  onEditManufacturerOptions,
                }}
                expandedSections={{
                  details: false,
                  options: true,
                  manufacturerOptions: true,
                }}
                optionsProps={{
                  options: {
                    variant: "text",
                    aboveOptionsContent: (
                      <Box mb={1}>
                        {renderAppLocation(
                          "ConfirmVehicleOptionsToSource-ChangeLaterText",
                          <APInfoText text="Options will not affect vehicle pricing and can be changed later." />
                        )}
                      </Box>
                    ),
                    filterable: false,
                    emptyMessage: "No options selected.",
                  },
                  manufacturerOptions: {
                    variant: "text",
                    filterable: false,
                    emptyMessage: "No manufacturer options selected.",
                  },
                }}
              />

              {!driverSourcedVehicle &&
                renderAppLocation(
                  "ConfirmVehicleOptionsToSource-SelectCorrectOptionsText",
                  <APInfoText text="Please ensure you select the correct vehicle and manufacturer options as this will affect pricing and availability." />
                )}

              {showVehiclePricingUpload ? (
                <APFileUpload
                  name="driverVehicleQuoteFile"
                  label="Upload Vehicle Quote"
                  control={control}
                  errors={errors}
                  validations={{}}
                />
              ) : (
                <APButtonLink
                  text="I have vehicle pricing"
                  onClick={onToggleVehiclePricingUpload}
                  noUnderline
                />
              )}

              {!driverSourcedVehicle && (
                <APCheckbox
                  name="confirm"
                  control={control}
                  label="I confirm that this is the vehicle I would like pricing for."
                  defaultValue={false}
                  validations={{ required: true }}
                  errors={errors}
                  hideRequired={true}
                />
              )}
            </APForm>
            <Box>
              <RestrictedAppScope scope="external">
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data?.opportunity?.id ?? undefined
                  }
                />
              </RestrictedAppScope>
            </Box>
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default VehicleSourcingConfirmConfiguration;
